import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Avatar, Dropdown, Layout, Menu, Space, Typography, message } from "antd";
import type { MenuProps } from "antd";
import { ApiOutlined, LogoutOutlined, StopOutlined, TeamOutlined, UserOutlined } from "@ant-design/icons";
import type { MeResponse } from "@coco-mailrelay/shared-types";
import { apiRequest } from "../lib/apiRequest";
import { useAuthStore } from "../store/auth.store";

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const NAV: { key: string; icon: React.ReactNode; label: string }[] = [
  { key: "/tenants", icon: <ApiOutlined />, label: "Tenants" },
  { key: "/members", icon: <TeamOutlined />, label: "Members" },
  { key: "/suppressions", icon: <StopOutlined />, label: "Suppressions" },
];

/** Authenticated shell: sidebar nav + header with the current admin (docs/305 §4.2). */
export default function AdminLayout(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const member = useAuthStore((s) => s.member);
  const setMember = useAuthStore((s) => s.setMember);
  const logout = useAuthStore((s) => s.logout);

  // Refresh the current admin from /auth/me on mount (keeps the store fresh).
  // A 401 here means the httpOnly cookie is gone/expired: apiRequest handles it
  // centrally (clears the persisted session + redirects to /login), so the
  // stale `isAuthenticated:true` can't strand the user in the logged-in shell.
  // The catch only swallows transient/non-401 errors.
  useEffect(() => {
    apiRequest<MeResponse>("/auth/me")
      .then((res) => setMember(res.member))
      .catch(() => undefined);
  }, [setMember]);

  const handleLogout = async (): Promise<void> => {
    try {
      await apiRequest("/auth/logout", { method: "POST" });
    } catch {
      // best effort
    }
    logout();
    message.success("Signed out");
    navigate("/login");
  };

  const selectedKey = NAV.map((n) => n.key).find((k) => location.pathname.startsWith(k)) ?? "/tenants";

  const userMenu: MenuProps["items"] = [
    { key: "logout", icon: <LogoutOutlined />, label: "Sign out", onClick: handleLogout },
  ];

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider theme="light" breakpoint="lg" collapsedWidth="0">
        <div style={{ height: 56, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600 }}>
          mailrelay
        </div>
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          onClick={({ key }) => navigate(key)}
          items={NAV.map((n) => ({ key: n.key, icon: n.icon, label: n.label }))}
        />
      </Sider>
      <Layout>
        <Header style={{ background: "#fff", display: "flex", justifyContent: "flex-end", alignItems: "center", paddingInline: 24 }}>
          <Dropdown menu={{ items: userMenu }}>
            <Space style={{ cursor: "pointer" }}>
              <Avatar size="small" icon={<UserOutlined />} />
              <Text>{member?.name || member?.email || "Admin"}</Text>
            </Space>
          </Dropdown>
        </Header>
        <Content style={{ margin: 24 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
